import Swal from 'sweetalert2'
import type { SweetAlertOptions } from 'sweetalert2'

// Estilos comunes para que todos los alerts compartan look & feel del CRM
const BASE: SweetAlertOptions = {
    confirmButtonColor: '#0ea5e9',
    cancelButtonColor: '#64748b',
    buttonsStyling: true,
    customClass: {
        popup: 'rounded-2xl shadow-2xl',
    },
    showClass: { popup: 'animate__animated animate__fadeInDown animate__faster' },
    hideClass: { popup: 'animate__animated animate__fadeOutUp animate__faster' },
}

export const swal = {
    success(title: string, text?: string) {
        return Swal.fire({
            ...BASE,
            icon: 'success',
            title,
            text,
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
        })
    },

    error(title: string, text?: string) {
        return Swal.fire({
            ...BASE,
            icon: 'error',
            title,
            text,
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'OK',
        })
    },

    warning(title: string, text?: string) {
        return Swal.fire({
            ...BASE,
            icon: 'warning',
            title,
            text,
            confirmButtonColor: '#f59e0b',
            confirmButtonText: 'OK',
        })
    },

    info(title: string, text?: string) {
        return Swal.fire({
            ...BASE,
            icon: 'info',
            title,
            text,
            confirmButtonText: 'OK',
        })
    },

    // Devuelve true si el usuario confirma, false si cancela
    async confirm(title: string, text?: string, opts?: { confirmText?: string; cancelText?: string; danger?: boolean }) {
        const res = await Swal.fire({
            ...BASE,
            icon: opts?.danger ? 'warning' : 'question',
            title,
            text,
            showCancelButton: true,
            confirmButtonColor: opts?.danger ? '#ef4444' : '#0ea5e9',
            cancelButtonColor: '#64748b',
            confirmButtonText: opts?.confirmText || 'Confirmar',
            cancelButtonText: opts?.cancelText || 'Cancelar',
            reverseButtons: true,
        })
        return res.isConfirmed
    },

    toast(title: string, icon: 'success' | 'error' | 'info' | 'warning' = 'success') {
        return Swal.fire({
            toast: true,
            position: 'top-end',
            icon,
            title,
            showConfirmButton: false,
            timer: 2500,
            timerProgressBar: true,
        })
    },
}

export default swal

/**
 * Reemplaza window.alert para que TODA llamada existente a alert(...) en la app
 * se renderice como un SweetAlert moderno. Detecta el tipo (success/error/warning/info)
 * por prefijos comunes (✅, ❌, ⚠️, ℹ️, "Error", "OK", etc.).
 *
 * window.confirm NO se sobreescribe porque es síncrono y SweetAlert es async.
 * Para reemplazar confirm(...) usa swal.confirm(...) directamente (es async).
 */
export function installSwalAsGlobalAlert() {
    if (typeof window === 'undefined') return
    if ((window as any).__swalAlertInstalled) return
    ;(window as any).__swalAlertInstalled = true

    window.alert = (message?: any) => {
        const txt = String(message ?? '')
        const lower = txt.toLowerCase()
        // Heurística para escoger el icono según el contenido
        if (txt.startsWith('✅') || lower.startsWith('ok ') || lower.includes('correctamente') || lower.includes('con éxito') || lower.includes('guardado')) {
            swal.success(txt.replace(/^[✅✔️]\s*/, '').trim() || 'Hecho')
        } else if (txt.startsWith('❌') || lower.startsWith('error') || lower.includes('fallo') || lower.includes('no se pudo') || lower.includes('inválido')) {
            swal.error('Error', txt.replace(/^[❌✖️]\s*/, '').trim())
        } else if (txt.startsWith('⚠️') || lower.startsWith('atención') || lower.includes('cuidado') || lower.includes('advertencia')) {
            swal.warning('Atención', txt.replace(/^⚠️\s*/, '').trim())
        } else {
            swal.info('Aviso', txt)
        }
    }
}

